import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, Info, ImageIcon, Loader2 } from 'lucide-react';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { updateUser } from '../../../graphql/mutations';
import { useCurrentUserStore } from '../../../application/store/zustand/useCurrentUserStore';
import {
  validateEmail,
  validatePhoneNumber,
} from '../../../application/routes/settings/utils/validation';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { FormField } from '../../components/ui/form-field';
import { useToast } from '../../components/ui/toast';

declare const global: {
  me: {
    id: string;
    imageName?: string;
    name: string;
  };
};

interface UserProfile {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  avatarID: string | null;
  externalName: string;
  externalEmail: string;
  externalPhoneNumber: string;
  externalTitle: string;
}

const EMPTY_USER: UserProfile = {
  id: '',
  name: '',
  email: '',
  phoneNumber: '',
  avatarID: null,
  externalName: '',
  externalEmail: '',
  externalPhoneNumber: '',
  externalTitle: '',
};

export function CustomerFacingProfilePage() {
  const [user, setUser] = useState<UserProfile>(EMPTY_USER);
  const [originalUser, setOriginalUser] = useState<UserProfile>(EMPTY_USER);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<{
    externalEmail?: string;
    externalPhoneNumber?: string;
  }>({});

  const userID = useCurrentUserStore((state) => state.userID) || global?.me?.id;
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasChanged = JSON.stringify(user) !== JSON.stringify(originalUser);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const formats = ['jpeg', 'jpg', 'png', 'gif'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !formats.includes(fileExtension)) {
      toast.error('Image format must be PNG, JPG, or GIF');
      return;
    }

    setIsUploading(true);
    try {
      // @ts-ignore
      const orgID = global?.me?.orgID;
      const storagePath = `company-info/profile-image/${orgID}/${Date.now()}_${
        file.name
      }`;

      await Storage.put(storagePath, file, {
        contentType: file.type,
      });

      const cloudFrontUrl = `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
        storagePath
      )}`;
      setPreviewUrl(cloudFrontUrl);
      setUser((prev) => ({ ...prev, avatarID: storagePath }));
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
    e.target.value = '';
  };

  useEffect(() => {
    async function fetchUser() {
      if (!userID) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const query = `
          query GetUser($id: ID!) {
            getUser(id: $id) {
              id
              name
              email
              phoneNumber
              avatarID
              externalPhoneNumber
              externalTitle
              externalName
              externalEmail
            }
          }
        `;

        const res = (await API.graphql({
          query,
          variables: { id: userID },
        })) as { data: { getUser: any } };

        const data = res.data.getUser;
        if (data) {
          const profile: UserProfile = {
            id: data.id,
            name: data.name || '',
            email: data.email || '',
            phoneNumber: data.phoneNumber || '',
            avatarID: data.avatarID || null,
            externalName: data.externalName || '',
            externalEmail: data.externalEmail || '',
            externalPhoneNumber: data.externalPhoneNumber || '',
            externalTitle: data.externalTitle || '',
          };
          setUser(profile);
          setOriginalUser(profile);
          if (profile.avatarID) {
            setPreviewUrl(
              `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                profile.avatarID
              )}`
            );
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchUser();
  }, [userID]);

  const updateField = useCallback((field: keyof UserProfile, value: string) => {
    setUser((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (field === 'externalEmail' || field === 'externalPhoneNumber') {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, []);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (user.externalEmail && !validateEmail(user.externalEmail)) {
      newErrors.externalEmail = 'Please enter a valid email address';
    }

    if (
      user.externalPhoneNumber &&
      !validatePhoneNumber(user.externalPhoneNumber)
    ) {
      newErrors.externalPhoneNumber =
        'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: user.id,
            externalName: user.externalName,
            externalEmail: user.externalEmail,
            externalPhoneNumber: user.externalPhoneNumber,
            externalTitle: user.externalTitle,
            avatarID: user.avatarID,
          },
        })
      );

      setOriginalUser(user);
      toast.success('Customer-facing profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error('Error updating customer-facing profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Customer-Facing Profile"
          avatar={{
            src: global.me?.imageName || undefined,
            name: global.me?.name,
          }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Customer-Facing Profile"
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-[976px] mx-auto space-y-10">
          {/* Public Information Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-6">
              <div className="flex items-center justify-center size-[60px] md:size-[100px] rounded-full bg-[hsl(var(--secondary)/0.2)]">
                <Globe className="size-6 md:size-8 text-[hsl(var(--secondary))]" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground">
                  Public information
                </h2>
                <p className="text-sm text-muted-foreground">
                  {user.email || 'admin@paiv.com'}
                </p>
              </div>
            </div>
            <button className="flex items-center justify-center size-10 md:size-12 rounded-lg border border-border">
              <Info className="size-5 md:size-6 text-muted-foreground" />
            </button>
          </div>

          {/* Content: Form + Image Upload */}
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left: Form Fields */}
            <div className="flex-1 space-y-6">
              {/* Section Header */}
              <div>
                <h3 className="text-base font-bold text-foreground">
                  Public profile
                </h3>
                <p className="text-base font-medium text-muted-foreground">
                  Visible to all your customers
                </p>
              </div>

              <div className="space-y-3 [&_label]:text-muted-foreground [&_label]:font-normal [&_input::placeholder]:text-muted-foreground">
                <FormField label="Name">
                  <Input
                    value={user.externalName}
                    onChange={(e) =>
                      updateField('externalName', e.target.value)
                    }
                    placeholder={user.name || 'Enter name'}
                  />
                </FormField>

                <FormField
                  label="Phone number"
                  error={errors.externalPhoneNumber}
                >
                  <Input
                    value={user.externalPhoneNumber}
                    onChange={(e) =>
                      updateField('externalPhoneNumber', e.target.value)
                    }
                    placeholder={user.phoneNumber || 'Enter phone number'}
                    className={
                      errors.externalPhoneNumber ? 'border-destructive' : ''
                    }
                  />
                </FormField>

                <FormField label="Email" error={errors.externalEmail}>
                  <Input
                    type="email"
                    value={user.externalEmail}
                    onChange={(e) =>
                      updateField('externalEmail', e.target.value)
                    }
                    placeholder={user.email || 'Enter email'}
                    className={errors.externalEmail ? 'border-destructive' : ''}
                  />
                </FormField>

                <FormField label="Role">
                  <Input
                    value={user.externalTitle}
                    onChange={(e) =>
                      updateField('externalTitle', e.target.value)
                    }
                    placeholder="e.g., Senior Estimator, Project Manager"
                  />
                </FormField>
              </div>
            </div>

            {/* Right: Public Profile Image */}
            <div className="w-full md:w-[317px] shrink-0 border border-border/60 rounded-xl p-6 flex flex-col gap-4 self-stretch">
              <h3 className="text-base font-bold text-foreground">
                Public profile image
              </h3>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 flex flex-col items-center justify-center bg-muted rounded-3xl gap-2.5 min-h-[200px] cursor-pointer hover:bg-muted/80 transition-colors overflow-hidden"
              >
                {isUploading ? (
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Profile preview"
                    className="size-full object-cover rounded-3xl"
                  />
                ) : (
                  <>
                    <ImageIcon className="size-6 text-muted-foreground" />
                    <p className="text-base font-semibold text-foreground">
                      Drag or upload
                    </p>
                    <p className="text-sm font-semibold text-muted-foreground">
                      PNG, JPG, GIF (Max 5 mb)
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </div>

          {/* Footer buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-8">
            <Button
              variant="outline"
              onClick={() => {
                setUser(originalUser);
                setErrors({});
              }}
              disabled={!hasChanged || isSaving}
              className="py-2.5 px-6 text-sm font-bold"
            >
              Discard
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanged || isSaving}
              className="py-2.5 px-6 text-sm font-bold"
            >
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
